import { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SlotPicker from '../shared/SlotPicker';
import DateCalendar from '../shared/DateCalendar';
import ContactForm from '../shared/ContactForm';
import ConfirmView from '../shared/ConfirmView';
import ProductCardList from '../shared/ProductCardList';
import StoreSelector from '../shared/StoreSelector';
import EmptyHint from '../shared/EmptyHint';
import { STEP_LABELS, buildVisibleSteps } from '../steps';
import type { AppointmentFormState } from '../useAppointmentForm';

interface Props {
  state: AppointmentFormState;
}

export default function DoctolibLayout({ state }: Props) {
  const {
    config,
    services,
    consultants,
    slots,
    selectedServiceId,
    selectedProductId,
    selectedConsultantId,
    selectedConsultant,
    selectedService,
    selectedProduct,
    productsForSelectedService,
    shouldShowProductStep,
    isProductRequired,
    selectedSlot,
    selectedDate,
    setSelectedDate,
    selectService,
    selectServiceAndAdvance,
    selectProductAndAdvance,
    selectConsultant,
    selectSlot,
    submitBooking,
    step,
    setStep,
    goNext,
    goBack,
    customer,
    error,
    captchaError,
    loading,
    bookingResult,
  } = state;
  // Task #2786 — auto-advance cancellabile: il timer che fa avanzare lo step
  // dopo una scelta (Data/Consulente/Orario) va annullato se l'utente torna
  // indietro entro la finestra (~200ms) o se il componente smonta, altrimenti
  // il goNext pendente riporterebbe avanti il wizard appena tornato indietro.
  const advanceTimer = useRef<number | null>(null);
  const cancelAdvance = () => {
    if (advanceTimer.current !== null) {
      clearTimeout(advanceTimer.current);
      advanceTimer.current = null;
    }
  };
  const scheduleAdvance = () => {
    cancelAdvance();
    advanceTimer.current = window.setTimeout(() => {
      advanceTimer.current = null;
      goNext();
    }, 200);
  };
  const handleBack = () => {
    cancelAdvance();
    goBack();
  };
  useEffect(() => cancelAdvance, []);
  // Task #515 + #525: il dropdown servizio è ora INLINE sotto la card della
  // tipologia attiva (lo step 'product' del wizard è stato rimosso).
  const products = productsForSelectedService || [];
  const showInlineProducts = shouldShowProductStep();
  const productRequired = isProductRequired();
  const productMissing = productRequired && !selectedProductId;
  // Task #2830 — quando la scelta sede è attiva, la sede è prerequisito: la
  // tipologia resta bloccata finché il cliente non sceglie una sede.
  const storeGateActive = !!config?.allowStoreChoice && !state.selectedStoreId;

  useEffect(() => {
    if (!config) return;
    const el = document.querySelector('.aw-root') as HTMLElement | null;
    if (el) {
      el.style.setProperty('--aw-primary', config.branding.primaryColor || '#0ea5e9');
      el.style.setProperty('--aw-accent', config.branding.accentColor || '#06b6d4');
      if (config.branding.fontFamily) el.style.setProperty('--aw-font', config.branding.fontFamily);
    }
  }, [config]);

  if (!config) {
    return (
      <div className="aw-root aw-shell aw-style-doctolib" style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Loader2 className="aw-spin" size={32} />
      </div>
    );
  }

  const themeClass = config.branding.defaultTheme === 'dark' ? 'aw-dark' : '';

  if (bookingResult) {
    return (
      <div className={`aw-root aw-shell aw-style-doctolib ${themeClass}`}>
        <ConfirmView state={state} />
      </div>
    );
  }

  // Task #525: lo step 'product' è stato rimosso (selezione inline).
  // Task #2846: lo step 'date' precede 'consultant' (in 'skill_shifts' la rosa è
  // ristretta ai turni del giorno scelto); 'slot' è ora la sola scelta orario.
  // Sequenza step condivisa con CalcomLayout (steps.ts).
  const stepsList = buildVisibleSteps(config);
  const currentIdx = stepsList.indexOf(step);

  return (
    <div className={`aw-root aw-shell aw-style-doctolib ${themeClass}`}>
      <header className="aw-header">
        {config.branding.logoUrl && <img src={config.branding.logoUrl} alt="" className="aw-logo" />}
        <div>
          <h2 className="aw-title">{config.name}</h2>
          {config.description && <p className="aw-subtitle">{config.description}</p>}
        </div>
      </header>

      {/* Stepper numerato vivace (Task #2847) — cerchi numerati connessi */}
      <div className="aw-stepper" aria-label="Progresso">
        {stepsList.map((s, i) => {
          const stepState = i === currentIdx ? 'active' : i < currentIdx ? 'done' : 'todo';
          return (
            <div key={s} className={`aw-stepper-item aw-stepper-${stepState}`}>
              <span className="aw-stepper-num">{i < currentIdx ? '✓' : i + 1}</span>
            </div>
          );
        })}
      </div>
      <p style={{ fontSize: 12, color: 'var(--aw-muted)', margin: '0 0 14px', fontWeight: 500 }}>
        Step {currentIdx + 1} di {stepsList.length} · {STEP_LABELS[step]}
      </p>

      {error && !captchaError && <div className="aw-error" role="alert">{error}</div>}

      <StoreSelector state={state} />

      <AnimatePresence mode="wait">
      <motion.div
        key={step}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
      >
      {step === 'service' && storeGateActive && (
        <div className="aw-hint" role="status" style={{ fontSize: 13, color: 'var(--aw-muted)', padding: '12px 14px', border: '1px dashed var(--aw-border, #e5e7eb)', borderRadius: 8 }}>
          Seleziona prima una sede per vedere le tipologie di appuntamento disponibili.
        </div>
      )}

      {step === 'service' && !storeGateActive && (
        <div className="aw-grid aw-grid-1">
          {services.map((s) => {
            const isSelected = selectedServiceId === s.id;
            const inlineProducts = isSelected ? products : [];
            const showInlineDropdown = isSelected && showInlineProducts && inlineProducts.length > 0;
            return (
              <div key={s.id}>
                <button
                  type="button"
                  className={`aw-card ${isSelected ? 'aw-selected' : ''}`}
                  onClick={() => { selectServiceAndAdvance(s.id); }}
                  style={showInlineDropdown ? { marginBottom: 8 } : undefined}
                >
                  <div className="aw-card-row">
                    <span style={{ width: 10, height: 10, borderRadius: 999, background: s.color || 'var(--aw-primary)' }} />
                    <h4>{s.name}</h4>
                    <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--aw-muted)' }}>{s.durationMinutes} min</span>
                  </div>
                  {s.description && <p>{s.description}</p>}
                </button>
                {showInlineDropdown && (
                  <ProductCardList
                    products={inlineProducts}
                    selectedProductId={selectedProductId}
                    onSelect={selectProductAndAdvance}
                    required={productRequired}
                    missing={productMissing}
                    showPrice={config.showPrice === true}
                  />
                )}
              </div>
            );
          })}
          {/* Task #2786 — auto-avanzamento: la scelta del servizio nel dropdown
              inline avanza da sola (selectProductAndAdvance), senza più click su
              "Avanti". In 'required' il dropdown resta mostrato finché non c'è
              una scelta; la selezione stessa avanza. */}
        </div>
      )}

      {/* Modalità prodotto opzionale ('always' / 'auto' con >1): il picker non
          obbliga; questo "Continua" consente l'avanzamento senza scegliere un
          prodotto (in 'required' non compare, scelta obbligatoria). */}
      {step === 'service' && !storeGateActive && selectedServiceId && showInlineProducts && !productRequired && (
        <div className="aw-actions">
          <button type="button" className="aw-btn aw-btn-primary" onClick={() => { cancelAdvance(); goNext(); }}>
            Continua
          </button>
        </div>
      )}

      {step === 'date' && (
        <div>
          {selectedService && (
            <div className="aw-summary">
              <div><span>Servizio</span><strong>{selectedService.name}</strong></div>
              {selectedProduct && <div><span>Prodotto</span><strong>{selectedProduct.name}</strong></div>}
            </div>
          )}
          {/* Task #2786 — coerenza con gli step Consulente/Orario: la scelta del
              giorno avanza da sola (auto-advance), niente barra Indietro/Continua
              dedicata. Resta il solo "← Indietro" generico in coda. */}
          <DateCalendar
            selected={selectedDate}
            onSelect={(d) => { setSelectedDate(d); scheduleAdvance(); }}
            maxAdvanceDays={config.rules.maxAdvanceDays ?? 90}
          />
        </div>
      )}

      {step === 'consultant' && config.consultantFilterMode !== 'any' && (
        <div className="aw-grid aw-grid-1">
          {!consultants.length && !loading.consultants && (
            <EmptyHint variant="consultants" />
          )}
          {consultants.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`aw-card ${selectedConsultantId === c.id ? 'aw-selected' : ''}`}
              onClick={() => { selectConsultant(c.id); scheduleAdvance(); }}
            >
              <div className="aw-consultant-card">
                {c.sitePhotoUrl ? (
                  <img src={c.sitePhotoUrl} alt={c.fullName} className="aw-avatar aw-avatar-lg" />
                ) : (
                  <div className="aw-avatar aw-avatar-lg">{c.firstName?.[0]}{c.lastName?.[0]}</div>
                )}
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontSize: 17 }}>{c.fullName}</h4>
                  {c.bio && <p style={{ marginBottom: 8 }}>{c.bio}</p>}
                  {c.skills?.length ? (
                    <div>
                      {c.skills.slice(0, 5).map((sk) => (
                        <span key={sk.name} className="aw-skill-badge">{sk.name}</span>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {step === 'slot' && (
        <div>
          <div className="aw-summary">
            {selectedConsultant && <div><span>Consulente</span><strong>{selectedConsultant.fullName}</strong></div>}
            {selectedService && <div><span>Servizio</span><strong>{selectedService.name}</strong></div>}
            <div>
              <span>Data</span>
              <strong>
                {new Date(`${selectedDate}T00:00:00`).toLocaleDateString('it-IT', {
                  weekday: 'long', day: 'numeric', month: 'long',
                })}
              </strong>
            </div>
          </div>
          <SlotPicker
            slots={slots}
            selected={selectedSlot}
            onSelect={(s) => { selectSlot(s); scheduleAdvance(); }}
            loading={loading.slots}
          />
        </div>
      )}

      {step === 'contact' && selectedSlot && (
        <div>
          {selectedService && (
            <div className="aw-summary">
              <div><span>Servizio</span><strong>{selectedService.name}</strong></div>
              {selectedConsultant && <div><span>Consulente</span><strong>{selectedConsultant.fullName}</strong></div>}
              <div>
                <span>Data e ora</span>
                <strong>
                  {new Date(selectedSlot.start).toLocaleString('it-IT', {
                    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                  })}
                </strong>
              </div>
              <div>
                <span>Durata</span>
                <strong>
                  {(Math.round((new Date(selectedSlot.end).getTime() - new Date(selectedSlot.start).getTime()) / 60000) || selectedService.durationMinutes)} min
                </strong>
              </div>
            </div>
          )}
          <ContactForm state={state} />
          <div className="aw-actions aw-actions-sticky">
            <button type="button" className="aw-btn aw-btn-ghost" onClick={handleBack}>Indietro</button>
            <button
              type="button"
              className="aw-btn aw-btn-primary"
              disabled={
                loading.booking ||
                !customer.name ||
                !customer.email ||
                !customer.phone ||
                !customer.privacyAccepted ||
                (config.allowStoreChoice && !state.selectedStoreId) ||
                (config.gdpr?.consents || []).some(
                  (c) => c.required
                    && c.key !== 'privacy_policy' && c.key !== 'privacy' && c.key !== 'privacy_acceptance' && c.key !== 'marketing'
                    && !customer.gdprConsents?.[c.key],
                ) ||
                ((!!config.antiAbuse.hcaptchaEnabled || !!config.antiAbuse.recaptchaEnabled) && !state.captchaToken)
              }
              onClick={() => submitBooking()}
            >
              {loading.booking ? <Loader2 className="aw-spin" size={16} /> : 'Conferma prenotazione'}
            </button>
          </div>
        </div>
      )}

      {step !== 'service' && step !== 'contact' && step !== 'confirm' && (
        <div className="aw-actions">
          <button type="button" className="aw-btn aw-btn-ghost" onClick={handleBack}>← Indietro</button>
        </div>
      )}
      </motion.div>
      </AnimatePresence>
    </div>
  );
}
